import {
  Qulite,
  enqueueFsEvent,
  type PersistedJob,
  JobStatus,
} from '@coderule/qulite';
import type Database from 'better-sqlite3';
import type { Logger } from 'pino';

export class Outbox {
  private readonly queue: Qulite;
  private readonly log: Logger;
  private readonly markKindStmt: Database.Statement;
  private readonly purgeLegacyStmt: Database.Statement;

  constructor(db: Database.Database, logger: Logger) {
    this.log = logger.child({ scope: 'outbox' });
    this.queue = new Qulite(db, {
      logger: this.log,
      defaultLeaseMs: 600_000,
      defaultMaxAttempts: 10,
    });
    this.markKindStmt = db.prepare(
      `UPDATE qulite_jobs SET kind = @kind WHERE dedupe_key = @dedupe_key`,
    );
    this.purgeLegacyStmt = db.prepare(
      `DELETE FROM qulite_jobs WHERE type = 'fs_control' AND (kind IS NULL OR kind = '')`,
    );
    const purged = this.purgeLegacyStmt.run().changes ?? 0;
    if (purged > 0) {
      this.log.warn({ purged }, 'Purged legacy fs_control jobs without kind');
    }

    // Log current queue counts to aid debugging when queue appears empty
    try {
      const counts = {
        pending: this.queue.countByStatus(JobStatus.Pending),
        processing: this.queue.countByStatus(JobStatus.Processing),
        done: this.queue.countByStatus(JobStatus.Done),
        failed: this.queue.countByStatus(JobStatus.Failed),
      };
      this.log.debug({ counts }, 'Outbox initialized');
    } catch {}
  }

  getQueue(): Qulite {
    return this.queue;
  }

  private markKind(dedupeKey: string, kind: string): void {
    this.markKindStmt.run({ dedupe_key: dedupeKey, kind });
  }

  enqueueSnapshot(rootId: string, delayMs = 0): void {
    const result = enqueueFsEvent(this.queue, {
      root_id: rootId,
      rel_path: '',
      kind: 'snapshot',
      delayMs,
      maxAttempts: 20,
      data: { root_id: rootId, kind: 'snapshot' },
    });
    this.markKind(`snapshot:${rootId}`, 'snapshot');
    if (result.changes > 0) {
      this.log.debug({ rootId }, 'Enqueued snapshot job');
    }
  }

  enqueueHeartbeat(rootId: string, delayMs = 0): void {
    const result = enqueueFsEvent(this.queue, {
      root_id: rootId,
      rel_path: '',
      kind: 'heartbeat',
      delayMs,
      maxAttempts: 5,
      data: { root_id: rootId, kind: 'heartbeat' },
    });
    this.markKind(`heartbeat:${rootId}`, 'heartbeat');
    if (result.changes > 0) {
      this.log.debug({ rootId }, 'Enqueued heartbeat job');
    }
  }

  claimFsControlJob(
    leaseOwner: string,
    leaseMs = 600_000,
  ): PersistedJob | undefined {
    return this.queue.claimNext({ type: 'fs_control', leaseOwner, leaseMs });
  }

  ack(jobId: number, leaseOwner: string): boolean {
    return this.queue.ack(jobId, leaseOwner);
  }

  retry(jobId: number, leaseOwner: string, delayMs?: number): boolean {
    return this.queue.retry(jobId, leaseOwner, delayMs);
  }

  fail(jobId: number, leaseOwner: string, error?: string): boolean {
    return this.queue.fail(jobId, leaseOwner, error);
  }

  requeueTimedOut(): number {
    return this.queue.requeueTimedOut();
  }
}
